import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Clock3,
  Plus,
  RefreshCw,
  Scissors,
  Users,
} from "lucide-react";
import { request } from "./FunctionalApp";
const api = async (path: string, init: RequestInit = {}) => {
  const r = await request(path, init),
    x = await r.json().catch(() => ({}));
  if (!r.ok)
    throw new Error(String(x.error || "Unable to save").replaceAll("_", " "));
  return x;
};
const send = (path: string, body: unknown, method = "POST") =>
  api(path, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
const pounds = (p: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    (p || 0) / 100,
  );
const initialTime = () => {
  const d = new Date(Date.now() + 86400000);
  d.setMinutes(0, 0, 0);
  return d.toISOString().slice(0, 16);
};
export function AppointmentsWorkspace() {
  const [data, setData] = useState<any>({
      staff: [],
      services: [],
      assignments: [],
      hours: [],
      blocks: [],
      settings: {},
      customers: [],
      appointments: [],
      users: [],
    }),
    [tab, setTab] = useState("Calendar"),
    [message, setMessage] = useState(""),
    [loading, setLoading] = useState(true);
  async function load() {
    setLoading(true);
    try {
      setData(await api("/api/appointments/bootstrap"));
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  const today = new Date().toISOString().slice(0, 10),
    future = useMemo(
      () =>
        data.appointments.filter(
          (a: any) => String(a.starts_at).slice(0, 10) >= today && !["CANCELLED","COMPLETED","NO_SHOW"].includes(a.status),
        ),
      [data.appointments, today],
    ),
    tabs = [
      "Calendar",
      ...(data.canBook ? ["New booking"] : []),
      ...(data.canManage ? ["Staff", "Services", "Reports", "Settings"] : []),
    ];
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Appointments</h1>
          <p>Staff calendars, services, availability and customer bookings.</p>
        </div>
        <button onClick={load}>
          <RefreshCw />
          Refresh
        </button>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <div className="appointment-tabs">
        {tabs.map((n) => (
          <button
            className={tab === n ? "selected" : ""}
            onClick={() => setTab(n)}
            key={n}
          >
            {n}
          </button>
        ))}
      </div>
      {loading ? (
        <p className="func-empty">Loading appointments…</p>
      ) : tab === "Calendar" ? (
        <Calendar
          appointments={future}
          staff={data.staff}
          update={async (id: string, status: string) => {
            await send(`/api/appointments/${id}/status`, { status }, "PATCH");
            setMessage("Appointment updated.");
            load();
          }}
        />
      ) : tab === "New booking" ? (
        <NewBooking
          data={data}
          saved={() => {
            setMessage("Appointment created.");
            setTab("Calendar");
            load();
          }}
        />
      ) : tab === "Staff" ? (
        <Staff
          data={data}
          saved={() => {
            setMessage("Staff availability saved.");
            load();
          }}
        />
      ) : tab === "Services" ? (
        <Services
          data={data}
          saved={() => {
            setMessage("Service saved.");
            load();
          }}
        />
      ) : tab === "Reports" ? (
        <Reports data={data} />
      ) : (
        <Settings
          settings={data.settings}
          saved={() => {
            setMessage("Booking settings saved.");
            load();
          }}
        />
      )}
    </>
  );
}
function Calendar({ appointments, staff, update }: any) {
  const [view, setView] = useState<"day"|"week"|"month">("week");
  const [offset, setOffset] = useState(0);
  const [selectedApt, setSelectedApt] = useState<any>(null);
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const HOUR_START = 8, HOUR_END = 20, HOUR_HEIGHT = 56;
  const statusColors: Record<string,string> = {CONFIRMED:"#0b9f91",CHECKED_IN:"#2563eb",IN_PROGRESS:"#7c3aed",COMPLETED:"#6b7280",CANCELLED:"#dc2626",NO_SHOW:"#9ca3af"};
  const weekStart = useMemo(() => { const d=new Date(today),day=d.getDay(); d.setDate(d.getDate()-(day===0?6:day-1)+(view==="week"?offset:0)*7); return d; }, [offset,today,view]);
  const dayBase = useMemo(() => { const d=new Date(today); d.setDate(d.getDate()+(view==="day"?offset:0)); return d; }, [offset,today,view]);
  const monthBase = useMemo(() => new Date(today.getFullYear(),today.getMonth()+(view==="month"?offset:0),1), [offset,today,view]);
  const weekDays = useMemo(() => Array.from({length:7},(_,i)=>{ const d=new Date(weekStart); d.setDate(d.getDate()+i); return d; }), [weekStart]);
  const monthDays = useMemo(() => {
    const days:(Date|null)[]=[], first=new Date(monthBase.getFullYear(),monthBase.getMonth(),1), last=new Date(monthBase.getFullYear(),monthBase.getMonth()+1,0), pad=first.getDay()===0?6:first.getDay()-1;
    for(let i=0;i<pad;i++) days.push(null);
    for(let d=1;d<=last.getDate();d++) days.push(new Date(monthBase.getFullYear(),monthBase.getMonth(),d));
    return days;
  }, [monthBase]);
  const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  function sameDay(a:Date,b:Date){return a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate();}
  function aptsForDay(date:Date){return appointments.filter((a:any)=>sameDay(new Date(a.starts_at),date));}
  function aptTop(a:any){const d=new Date(a.starts_at);return((d.getHours()-HOUR_START)+d.getMinutes()/60)*HOUR_HEIGHT;}
  function aptHeight(a:any){const s=new Date(a.starts_at),e=new Date(a.customer_ends_at||a.starts_at);return Math.max(((e.getTime()-s.getTime())/3600000)*HOUR_HEIGHT,24);}
  const navLabel = view==="day"?dayBase.toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"}):view==="week"?`${weekStart.toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – ${new Date(weekStart.getTime()+6*86400000).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}`:monthBase.toLocaleDateString("en-GB",{month:"long",year:"numeric"});
  function DayCol({date}:{date:Date}) {
    const isT=sameDay(date,today);
    return (
      <div className={`appt-cal-day-col ${isT?"appt-cal-today-col":""}`} style={{height:(HOUR_END-HOUR_START)*HOUR_HEIGHT}}>
        {Array.from({length:HOUR_END-HOUR_START},(_,i)=><div key={i} className="appt-cal-hour-line" style={{top:i*HOUR_HEIGHT}} />)}
        {aptsForDay(date).map((a:any)=>(
          <div key={a.id} className="appt-cal-event" style={{top:aptTop(a),height:aptHeight(a),background:statusColors[a.status]||"#0b9f91"}} onClick={()=>setSelectedApt(a)}>
            <b>{new Date(a.starts_at).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</b>
            <span>{a.customer_name||"Walk-in"}</span>
            <small>{a.service_name}</small>
          </div>
        ))}
      </div>
    );
  }
  return (
    <section className="func-panel appt-cal-panel">
      <div className="appt-cal-nav">
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button className="secondary" onClick={()=>setOffset(0)}>Today</button>
          <button className="secondary" onClick={()=>setOffset(o=>o-1)}>‹</button>
          <button className="secondary" onClick={()=>setOffset(o=>o+1)}>›</button>
          <span style={{fontWeight:700,fontSize:15}}>{navLabel}</span>
        </div>
        <div className="appt-cal-view-toggle">
          <button className={view==="day"?"selected":""} onClick={()=>{setView("day");setOffset(0);}}>Day</button>
          <button className={view==="week"?"selected":""} onClick={()=>{setView("week");setOffset(0);}}>Week</button>
          <button className={view==="month"?"selected":""} onClick={()=>{setView("month");setOffset(0);}}>Month</button>
        </div>
      </div>
      {(view==="day"||view==="week")&&(
        <div className="appt-cal-scroll">
          <div className="appt-cal-time-col">{Array.from({length:HOUR_END-HOUR_START},(_,i)=><div key={i} className="appt-cal-time-label" style={{top:i*HOUR_HEIGHT+2}}>{String(HOUR_START+i).padStart(2,"0")}:00</div>)}</div>
          {view==="day"&&<div style={{flex:1,position:"relative"}}><DayCol date={dayBase} /></div>}
          {view==="week"&&(
            <div className="appt-cal-week-cols">
              {weekDays.map((d,i)=>(
                <div key={i} className="appt-cal-week-col">
                  <div className={`appt-cal-week-day-header ${sameDay(d,today)?"appt-cal-today-header":""}`}>
                    <span>{DAYS[i]}</span><span className={sameDay(d,today)?"appt-cal-today-num":""}>{d.getDate()}</span>
                  </div>
                  <DayCol date={d} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {view==="month"&&(
        <div className="appt-cal-month">
          {DAYS.map(d=><div key={d} className="appt-cal-month-header">{d}</div>)}
          {monthDays.map((d,i)=>{
            if(!d) return <div key={`e${i}`} className="appt-cal-month-cell appt-cal-month-empty" />;
            const isT=sameDay(d,today),dayApts=aptsForDay(d);
            return (
              <div key={d.toISOString()} className={`appt-cal-month-cell ${isT?"appt-cal-today-col":""}`}>
                <span className={`appt-cal-month-date ${isT?"appt-cal-today-num":""}`}>{d.getDate()}</span>
                <div className="appt-cal-month-events">
                  {dayApts.slice(0,3).map((a:any)=><div key={a.id} className="appt-cal-month-event" style={{background:statusColors[a.status]||"#0b9f91"}} onClick={()=>setSelectedApt(a)}>{new Date(a.starts_at).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})} {a.customer_name||"Walk-in"}</div>)}
                  {dayApts.length>3&&<div className="appt-cal-month-more">+{dayApts.length-3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {selectedApt&&(
        <div className="appt-cal-popover-overlay" onClick={()=>setSelectedApt(null)}>
          <div className="appt-cal-popover" onClick={e=>e.stopPropagation()}>
            <b style={{fontSize:16}}>{selectedApt.customer_name||"Walk-in customer"}</b>
            <span>{selectedApt.service_name} with {selectedApt.staff_name}</span>
            <span>{new Date(selectedApt.starts_at).toLocaleString("en-GB",{weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
            <span>{pounds(selectedApt.original_price_pence-selectedApt.discount_pence)} · {String(selectedApt.payment_status).replaceAll("_"," ")}</span>
            <label style={{display:"flex",flexDirection:"column",gap:4,fontSize:13}}>Status
              <select value={selectedApt.status} onChange={e=>{update(selectedApt.id,e.target.value);setSelectedApt({...selectedApt,status:e.target.value});}}>
                <option value="CONFIRMED">Confirmed</option><option value="CHECKED_IN">Checked in</option>
                <option value="IN_PROGRESS">In progress</option><option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option><option value="NO_SHOW">No show</option>
              </select>
            </label>
            <button className="secondary" onClick={()=>setSelectedApt(null)}>Close</button>
          </div>
        </div>
      )}
    </section>
  );
}

function NewBooking({ data, saved }: any) {
  const [form, setForm] = useState({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      serviceId: data.services[0]?.id || "",
      staffId: "",
      startsAt: initialTime(),
      source: "PHONE",
      paymentChoice: "PAY_ON_DAY",
      notes: "",
    }),
    [slots, setSlots] = useState<any[]>([]),
    [error, setError] = useState("");
  const qualified = data.staff.filter((s: any) =>
    data.assignments.some(
      (a: any) => a.staff_id === s.id && a.service_id === form.serviceId,
    ),
  );
  async function find() {
    try {
      const q = new URLSearchParams({
        serviceId: form.serviceId,
        date: form.startsAt.slice(0, 10),
        ...(form.staffId ? { staffId: form.staffId } : {}),
      });
      setSlots((await api(`/api/appointments/slots?${q}`)).slots);
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await send("/api/appointments", {
        ...form,
        startsAt: new Date(form.startsAt).toISOString(),
      });
      saved();
    } catch (x: any) {
      setError(x.message);
    }
  }
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>
          <Plus />
          Create appointment or walk-in
        </h2>
      </div>
      <form className="appointment-form" onSubmit={submit}>
        <label>
          Customer name
          <input
            required
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={form.customerEmail}
            onChange={(e) =>
              setForm({ ...form, customerEmail: e.target.value })
            }
          />
        </label>
        <label>
          Phone
          <input
            value={form.customerPhone}
            onChange={(e) =>
              setForm({ ...form, customerPhone: e.target.value })
            }
          />
        </label>
        <label>
          Service
          <select
            required
            value={form.serviceId}
            onChange={(e) =>
              setForm({ ...form, serviceId: e.target.value, staffId: "" })
            }
          >
            <option value="">Choose service</option>
            {data.services.map((s: any) => (
              <option value={s.id} key={s.id}>
                {s.name} — {pounds(s.price_pence)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Staff
          <select
            value={form.staffId}
            onChange={(e) => setForm({ ...form, staffId: e.target.value })}
          >
            <option value="">Any available staff</option>
            {qualified.map((s: any) => (
              <option value={s.id} key={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date and time
          <input
            type="datetime-local"
            required
            value={form.startsAt}
            onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
          />
        </label>
        <label>
          Source
          <select
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
          >
            <option>PHONE</option>
            <option>IN_PERSON</option>
            <option>WALK_IN</option>
            <option>ONLINE</option>
          </select>
        </label>
        <label>
          Payment
          <select
            value={form.paymentChoice}
            onChange={(e) =>
              setForm({ ...form, paymentChoice: e.target.value })
            }
          >
            <option value="PAY_ON_DAY">Pay on day</option>
            <option value="DEPOSIT">Deposit online</option>
            <option value="FULL_ONLINE">Pay online in full</option>
          </select>
        </label>
        <label className="wide">
          Notes
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>
        <div className="wide appointment-actions">
          <button type="button" className="secondary" onClick={find}>
            <Clock3 />
            Check availability
          </button>
          <button disabled={!form.staffId}>Create appointment</button>
        </div>
        {error ? <p className="wide auth-error">{error}</p> : null}
      </form>
      {slots.length ? (
        <div className="slot-list">
          {slots.slice(0, 24).map((s) => (
            <button
              type="button"
              key={`${s.staffId}-${s.startsAt}`}
              onClick={() =>
                setForm({
                  ...form,
                  staffId: s.staffId,
                  startsAt: new Date(s.startsAt).toISOString().slice(0, 16),
                })
              }
            >
              {new Date(s.startsAt).toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
              })}
              <small>{s.staffName}</small>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
function Staff({ data, saved }: any) {
  const [form, setForm] = useState({
      name: "",
      jobTitle: "",
      bookable: true,
      userId: null as string | null,
    }),
    [selected, setSelected] = useState(""),
    [serviceId, setServiceId] = useState(""),
    [override, setOverride] = useState({ price: "", duration: "" }),
    [schedule, setSchedule] = useState({ days: [1, 2, 3, 4, 5], start: "09:00", end: "17:00" }),
    [block, setBlock] = useState({
      startsAt: initialTime(),
      endsAt: initialTime(),
      kind: "TIME_OFF",
      note: "",
    });
  async function create(e: FormEvent) {
    e.preventDefault();
    await send("/api/appointments/staff", form);
    setForm({ name: "", jobTitle: "", bookable: true, userId: null });
    saved();
  }
  async function assign() {
    if (!selected || !serviceId) return;
    await send(
      `/api/appointments/staff/${selected}/services/${serviceId}`,
      {
        pricePenceOverride: override.price
          ? Math.round(Number(override.price) * 100)
          : null,
        durationMinutesOverride: override.duration
          ? Number(override.duration)
          : null,
      },
      "PUT",
    );
    saved();
  }
  async function saveHours() {
    if (!selected || !schedule.days.length) return;
    await send(`/api/appointments/staff/${selected}/hours`, { hours: schedule.days.map((weekday) => ({ weekday, start: schedule.start, end: schedule.end })) }, "PUT");
    saved();
  }
  async function addBlock() {
    if (!selected) return;
    await send(`/api/appointments/staff/${selected}/blocks`, {
      ...block,
      startsAt: new Date(block.startsAt).toISOString(),
      endsAt: new Date(block.endsAt).toISOString(),
    });
    saved();
  }
  return (
    <div className="appointment-columns">
      <section className="func-panel">
        <div className="func-table-head">
          <h2>
            <Users />
            Staff and time off
          </h2>
        </div>
        {data.staff.map((s: any) => (
          <article key={s.id}>
            <span>
              <b>{s.name}</b>
              <small>{s.job_title || "Bookable staff"}</small>
            </span>
            <em>
              {data.assignments.filter((a: any) => a.staff_id === s.id).length}{" "}
              services
            </em>
          </article>
        ))}
        {data.blocks.map((b: any) => (
          <article key={b.id}>
            <span>
              <b>
                {b.staff_name}: {String(b.kind).replaceAll("_", " ")}
              </b>
              <small>
                {new Date(b.starts_at).toLocaleString("en-GB")} –{" "}
                {new Date(b.ends_at).toLocaleString("en-GB")}
              </small>
            </span>
            <button
              className="secondary"
              onClick={async () => {
                await api(`/api/appointments/blocks/${b.id}`, {
                  method: "DELETE",
                });
                saved();
              }}
            >
              Remove
            </button>
          </article>
        ))}
      </section>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Add staff</h2>
        </div>
        <form className="appointment-form single" onSubmit={create}>
          <label>
            Name
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            Job title
            <input
              value={form.jobTitle}
              onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
            />
          </label>
          <label>
            Optional login account
            <select
              value={form.userId || ""}
              onChange={(e) =>
                setForm({ ...form, userId: e.target.value || null })
              }
            >
              <option value="">No login linked</option>
              {(data.users || []).map((u: any) => (
                <option value={u.id} key={u.id}>
                  {u.name} · {u.email}
                </option>
              ))}
            </select>
          </label>
          <button>Add staff member</button>
        </form>
        <div className="appointment-assign">
          <h3>Assign service</h3>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Choose staff</option>
            {data.staff.map((s: any) => (
              <option value={s.id} key={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
          >
            <option value="">Choose service</option>
            {data.services.map((s: any) => (
              <option value={s.id} key={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Override price £"
            value={override.price}
            onChange={(e) =>
              setOverride({ ...override, price: e.target.value })
            }
          />
          <input
            type="number"
            placeholder="Override minutes"
            value={override.duration}
            onChange={(e) =>
              setOverride({ ...override, duration: e.target.value })
            }
          />
          <button onClick={assign}>Assign</button>
          <h3>Regular working hours</h3>
          <div className="schedule-days">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((name, weekday) => (
              <label key={name}>
                <input type="checkbox" checked={schedule.days.includes(weekday)} onChange={() => setSchedule({ ...schedule, days: schedule.days.includes(weekday) ? schedule.days.filter((d) => d !== weekday) : [...schedule.days, weekday].sort() })} />
                {name}
              </label>
            ))}
          </div>
          <label>Starts<input type="time" value={schedule.start} onChange={(e) => setSchedule({ ...schedule, start: e.target.value })} /></label>
          <label>Finishes<input type="time" value={schedule.end} onChange={(e) => setSchedule({ ...schedule, end: e.target.value })} /></label>
          <button onClick={saveHours}>Save regular hours</button>
          <h3>Block time off</h3>
          <input
            type="datetime-local"
            value={block.startsAt}
            onChange={(e) => setBlock({ ...block, startsAt: e.target.value })}
          />
          <input
            type="datetime-local"
            value={block.endsAt}
            onChange={(e) => setBlock({ ...block, endsAt: e.target.value })}
          />
          <select
            value={block.kind}
            onChange={(e) => setBlock({ ...block, kind: e.target.value })}
          >
            <option>TIME_OFF</option>
            <option>BREAK</option>
            <option>EXTRA_SHIFT</option>
          </select>
          <input
            placeholder="Reason (optional)"
            value={block.note}
            onChange={(e) => setBlock({ ...block, note: e.target.value })}
          />
          <button onClick={addBlock}>Add calendar block</button>
        </div>
      </section>
    </div>
  );
}
function Services({ data, saved }: any) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    durationMinutes: 45,
    bufferMinutes: 0,
    pricePence: 3500,
    depositType: "NONE",
    depositValue: 0,
    onlineDiscountType: "NONE",
    onlineDiscountValue: 0,
    cmmsAssetId: "",
  });
  const [assets, setAssets] = useState<any[]>([]);
  useEffect(() => {
    request("/api/cmms/assets").then(async r => { if (r.ok) { const d = await r.json(); setAssets(d.assets||[]); } }).catch(()=>{});
  }, []);
  async function create(e: FormEvent) {
    e.preventDefault();
    await send("/api/appointments/services", form);
    setForm({ ...form, name: "", description: "", cmmsAssetId: "" });
    saved();
  }
  return (
    <div className="appointment-columns">
      <section className="func-panel">
        <div className="func-table-head">
          <h2>
            <Scissors />
            Services
          </h2>
        </div>
        {data.services.map((s: any) => (
          <article key={s.id}>
            <span>
              <b>{s.name}</b>
              <small>
                {s.duration_minutes} minutes + {s.buffer_minutes} buffer
              </small>
            </span>
            <em>{pounds(s.price_pence)}</em>
          </article>
        ))}
      </section>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Add service</h2>
        </div>
        <form className="appointment-form" onSubmit={create}>
          <label>
            Name
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            Price (£)
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.pricePence / 100}
              onChange={(e) =>
                setForm({
                  ...form,
                  pricePence: Math.round(Number(e.target.value) * 100),
                })
              }
            />
          </label>
          <label>
            Customer minutes
            <input
              type="number"
              min="5"
              value={form.durationMinutes}
              onChange={(e) =>
                setForm({ ...form, durationMinutes: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Cleanup buffer
            <input
              type="number"
              min="0"
              value={form.bufferMinutes}
              onChange={(e) =>
                setForm({ ...form, bufferMinutes: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Deposit
            <select
              value={form.depositType}
              onChange={(e) =>
                setForm({ ...form, depositType: e.target.value })
              }
            >
              <option>NONE</option>
              <option>FIXED</option>
              <option>PERCENT</option>
            </select>
          </label>
          <label>
            Deposit value
            <input
              type="number"
              min="0"
              value={form.depositValue}
              onChange={(e) =>
                setForm({ ...form, depositValue: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Online discount
            <select
              value={form.onlineDiscountType}
              onChange={(e) =>
                setForm({ ...form, onlineDiscountType: e.target.value })
              }
            >
              <option>NONE</option>
              <option>FIXED</option>
              <option>PERCENT</option>
            </select>
          </label>
          <label>
            {form.onlineDiscountType === "FIXED"
              ? "Online discount (£)"
              : form.onlineDiscountType === "PERCENT"
                ? "Online discount (%)"
                : "Discount value"}
            <input
              type="number"
              min="0"
              max={form.onlineDiscountType === "PERCENT" ? 100 : undefined}
              step={form.onlineDiscountType === "FIXED" ? "0.01" : "1"}
              value={
                form.onlineDiscountType === "FIXED"
                  ? form.onlineDiscountValue / 100
                  : form.onlineDiscountValue
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  onlineDiscountValue:
                    form.onlineDiscountType === "FIXED"
                      ? Math.round(Number(e.target.value) * 100)
                      : Number(e.target.value),
                })
              }
            />
          </label>
          <label className="wide">
            Description
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </label>
          {assets.length > 0 && (
            <label>
              Link to CMMS asset (optional)
              <select value={form.cmmsAssetId} onChange={e => setForm({...form, cmmsAssetId: e.target.value})}>
                <option value="">No asset linked</option>
                {assets.map((a: any) => <option key={a.id} value={a.id}>{a.name}{a.external_ref ? ` (${a.external_ref})` : ""}</option>)}
              </select>
              <small style={{color:"var(--muted)",fontSize:11}}>If linked, this service will be blocked automatically when the asset goes out of service.</small>
            </label>
          )}
          <button>Create service</button>
        </form>
      </section>
    </div>
  );
}

function Reports({ data }: any) {
  const completed = data.appointments.filter(
      (a: any) => a.status === "COMPLETED",
    ),
    cancelled = data.appointments.filter((a: any) => a.status === "CANCELLED"),
    revenue = completed.reduce(
      (sum: number, a: any) =>
        sum +
        Number(a.original_price_pence || 0) -
        Number(a.discount_pence || 0),
      0,
    );
  return (
    <>
      <div className="seat-grid">
        <div className="func-kpi">
          <span>Bookings shown</span>
          <strong>{data.appointments.length}</strong>
        </div>
        <div className="func-kpi">
          <span>Completed</span>
          <strong>{completed.length}</strong>
        </div>
        <div className="func-kpi">
          <span>Cancelled</span>
          <strong>{cancelled.length}</strong>
        </div>
        <div className="func-kpi">
          <span>Booked value</span>
          <strong>{pounds(revenue)}</strong>
        </div>
      </div>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Service performance</h2>
        </div>
        {data.services.map((s: any) => {
          const jobs = data.appointments.filter(
            (a: any) => a.service_id === s.id,
          );
          return (
            <article key={s.id}>
              <span>
                <b>{s.name}</b>
                <small>{jobs.length} bookings</small>
              </span>
              <em>
                {pounds(
                  jobs.reduce(
                    (n: number, a: any) =>
                      n + Number(a.original_price_pence || 0),
                    0,
                  ),
                )}
              </em>
            </article>
          );
        })}
      </section>
    </>
  );
}
function Settings({ settings, saved }: any) {
  const [f, setF] = useState({
    onlineBookingEnabled: Boolean(settings.online_booking_enabled),
    bookingIntervalMinutes: Number(settings.booking_interval_minutes || 15),
    changesCutoffHours: Number(settings.changes_cutoff_hours || 24),
    journey: settings.journey || "SERVICE_FIRST",
    allowAnyStaff: Boolean(settings.allow_any_staff),
    kioskEnabled: Boolean(settings.kiosk_enabled),
    kioskAuthMode: settings.kiosk_auth_mode || "FULL_LOGIN",
    kioskPin: "",
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    await send("/api/appointments/settings", f, "PUT");
    saved();
  }
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>Online booking settings</h2>
        <a href="/book" target="_blank">Open public booking page</a>
      </div>
      <form className="appointment-form" onSubmit={submit}>
        <label className="check-row">
          <input type="checkbox" checked={f.onlineBookingEnabled}
            onChange={(e) => setF({ ...f, onlineBookingEnabled: e.target.checked })} />
          Accept online bookings
        </label>
        {f.onlineBookingEnabled && (
          <div style={{background:"#f5f8fa",borderRadius:8,padding:"14px 16px",display:"grid",gap:8}}>
            <b style={{fontSize:13,color:"#102a43"}}>Embed on your website</b>
            <p style={{margin:0,fontSize:12,color:"#506573"}}>Paste this into any page on your website to show the booking form inline.</p>
            <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
              <code style={{flex:1,background:"#fff",border:"1px solid #dce6ea",borderRadius:6,padding:"10px 12px",fontSize:11,color:"#102a43",wordBreak:"break-all",lineHeight:1.6,display:"block"}}>
                {`<iframe src="${window.location.origin}/book" width="100%" height="700" frameborder="0" style="border:none;border-radius:8px;" title="Book an appointment"></iframe>`}
              </code>
              <button type="button" className="secondary" style={{whiteSpace:"nowrap"}}
                onClick={() => navigator.clipboard.writeText(`<iframe src="${window.location.origin}/book" width="100%" height="700" frameborder="0" style="border:none;border-radius:8px;" title="Book an appointment"></iframe>`)}>
                Copy
              </button>
            </div>
            <p style={{margin:0,fontSize:11,color:"#8a9ca8"}}>Direct link: <a href="/book" target="_blank">{window.location.origin}/book</a></p>
          </div>
        )}
        <label className="check-row">
          <input
            type="checkbox"
            checked={f.allowAnyStaff}
            onChange={(e) => setF({ ...f, allowAnyStaff: e.target.checked })}
          />
          Allow “any available staff”
        </label>
        <label>
          Booking intervals (minutes)
          <input
            type="number"
            min="5"
            max="120"
            value={f.bookingIntervalMinutes}
            onChange={(e) =>
              setF({ ...f, bookingIntervalMinutes: Number(e.target.value) })
            }
          />
        </label>
        <label>
          Change/cancel cutoff (hours)
          <input
            type="number"
            min="0"
            max="720"
            value={f.changesCutoffHours}
            onChange={(e) =>
              setF({ ...f, changesCutoffHours: Number(e.target.value) })
            }
          />
        </label>
        <label>
          Booking journey
          <select
            value={f.journey}
            onChange={(e) => setF({ ...f, journey: e.target.value })}
          >
            <option value="SERVICE_FIRST">Choose service first</option>
            <option value="STAFF_FIRST">Choose staff first</option>
          </select>
        </label>
        <hr style={{border:0,borderTop:"1px solid #e8eef2",margin:"8px 0"}} />
        <h3 style={{margin:"4px 0 8px",fontSize:14,fontWeight:700,color:"#102a43"}}>Walk-in kiosk</h3>
        <label className="check-row">
          <input type="checkbox" checked={f.kioskEnabled}
            onChange={e => setF({ ...f, kioskEnabled: e.target.checked })} />
          Enable walk-in kiosk page
        </label>
        {f.kioskEnabled && <>
          <label>
            Kiosk URL — open this on your tablet
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input readOnly value={`${window.location.origin}/kiosk`} style={{flex:1,background:"#f5f8fa",color:"#506573"}} />
              <button type="button" className="secondary" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/kiosk`)}>Copy</button>
              <a href="/kiosk" target="_blank"><button type="button" className="secondary">Open</button></a>
            </div>
          </label>
          <label>
            Staff access mode
            <select value={f.kioskAuthMode} onChange={e => setF({ ...f, kioskAuthMode: e.target.value })}>
              <option value="FULL_LOGIN">Staff sign in with their own email and password</option>
              <option value="MANAGER_PIN">Manager PIN — single shared PIN to exit kiosk</option>
            </select>
          </label>
          {f.kioskAuthMode === "MANAGER_PIN" && (
            <label>
              {settings.kiosk_pin_hash ? "Change manager PIN (leave blank to keep current)" : "Set manager PIN (4–6 digits)"}
              <input type="password" inputMode="numeric" minLength={4} maxLength={6}
                placeholder="Enter 4–6 digit PIN" value={f.kioskPin}
                onChange={e => setF({ ...f, kioskPin: e.target.value.replace(/\D/g,"") })} />
            </label>
          )}
        </>}
        <button>Save settings</button>
      </form>
    </section>
  );
}
